import { Box, Link, Typography } from '@mui/material'

import { Feedback } from '@/components/Feedback'
import type { FlagFeedbackPayload } from '@/lib/apollo/mutation/patchFlagFeedback/mutation'
import type { VerifAIFlag } from '@/lib/apollo/query/getVerifAIFlags/query'

export interface Props {
  submitFlagFeedback: (payload: FlagFeedbackPayload) => Promise<void>
  loadingFlagFeedback?: boolean
  flagFeedbackError?: Error
  flags: VerifAIFlag[]
  userId: string
  subjectId: string
}

const getSeverity = (riskScore: number): 'error' | 'warning' | 'info' => {
  if (riskScore >= 80) return 'error'
  if (riskScore >= 50) return 'warning'
  return 'info'
}

const severityStyles = {
  error: { color: 'red' },
  warning: { color: 'orange' },
  info: { color: 'green' },
}

export const VerifAIFlags = ({
  flags,
  userId,
  subjectId,
  submitFlagFeedback,
  loadingFlagFeedback,
  flagFeedbackError,
}: Props) => {
  return flags.length > 0 ? (
    <Box>
      {flags.map((flag, i) => (
        <Box
          key={i}
          sx={{
            mt: 2,
            p: 2,
            border: '1px solid',
            borderColor: '#111111',
            borderRadius: 1,
            borderLeft: '10px solid',
            borderLeftColor: severityStyles[getSeverity(flag.result.riskScore)].color,
          }}
        >
          <Typography variant="body1" fontWeight="bold">
            Risk Score: {flag.result.riskScore}
          </Typography>
          <Typography variant="body1" mb={2}>
            {flag.result.comment}
          </Typography>
          <Feedback
            flag={flag}
            userId={userId}
            subjectId={subjectId}
            submitFeedback={submitFlagFeedback}
            loadingFeedback={loadingFlagFeedback}
            feedbackError={flagFeedbackError}
          />
        </Box>
      ))}
      <Typography
        variant={'body2'}
        color="textSecondary"
        sx={{ mt: 2 }}
        textAlign={'center'}
      >
        <b>VerifAI can make mistakes.</b> Check important info. Help us improve VerifAI by
        providing your{' '}
        <Link
          href="https://docs.google.com/forms/d/e/1FAIpQLSeypCxAPH3jFPiQ3Mia7wU_vlwg6BPUwW3POq6t9gYrS0-cFw/viewform?usp=header"
          target="_blank"
          rel="noopener noreferrer"
        >
          suggestions for data points we could use
        </Link>{' '}
        to reduce fraud or make referencing easier.
      </Typography>
    </Box>
  ) : (
    <Typography variant={'body1'} color="textSecondary" sx={{ mt: 2 }}>
      There are no VerifAI flags for this subject.
    </Typography>
  )
}
